import {
  Box,
  HStack,
  Text,
  VStack,
  Link,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import { useState, useMemo, useRef, useEffect } from 'react';
import moment from 'moment';
import { Panel, PanelHeader, PanelHeading } from '@gamma/ui/layout';
import { i18n } from '@investigator/shared/localization';
import { formatDateRangeDisplay } from '@investigator/shared/utils';
import { DetectionSummaryInformationCard } from './DetectionSummaryInformationCard';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import {
  Detection,
  DetectionCountsByMitreTactic,
  GetFirstMostSevereAndLatestDetectionsByEntityNameQuery,
} from '@investigator/shared/generated/graphql';
import { ChatGPTIcon } from '@investigator/features/entities/components/ChatGPTIcon';
import { useDetectionSummaryContext } from '../../context';
import { useEntitiesContext } from '@investigator/features/entities/context';
import { useEntityAgenticTriageReportContext } from '@investigator/features/entities/context/EntityAgenticTriageReportContext';
import {
  MitreTacticsPopover,
  TriageStatusPopover,
} from '@investigator/features/entities/components/HighestRiskEntitiesTable';
import { SeverityBreakdownBarChart } from '@investigator/features/entities/components/SeverityBreakdownBarChart';
import { DetectionsActivity } from '@investigator/features/entities/components/EntityAnalysisModal/DetectionsActivity/DetectionsActivity';
import { useParseBackticks } from '@investigator/shared/hooks';
import { AlertInfo } from '@investigator/shared/generated';

const copy = i18n.pages.entities.detectionSummaryInformation;

type DetectionSummaryDetection = NonNullable<
  GetFirstMostSevereAndLatestDetectionsByEntityNameQuery['getFirstMostSevereAndLatestDetectionsByEntityName']
>['first_detection'];

export const DetectionSummaryInformation = (): JSX.Element | null => {
  const { parseBackticks } = useParseBackticks();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  // Get detection summary data from context
  const { entityDetectionSummary: detectionSummary } =
    useDetectionSummaryContext();

  // Get selected entity from context
  const { selectedEntity } = useEntitiesContext();

  // Get report context for AI insights and detection descriptions
  const { report, error: reportError } = useEntityAgenticTriageReportContext();

  // Use normalized dates from selectedEntity (context handles fallback to global dates)
  const selectedEntityStart = selectedEntity?.start ?? 0;
  const selectedEntityEnd = selectedEntity?.end ?? 0;

  // Calculate date range display from actual timestamps being used
  const dateRangeDisplay = formatDateRangeDisplay(
    selectedEntityStart,
    selectedEntityEnd,
  );

  // Extract entity details
  const entityName = selectedEntity?.entityName ?? '';
  const tenantEntity = selectedEntity?.detectionData?.tenant_entity ?? '';
  const tenantId = selectedEntity?.tenantInfo?.tenant_id ?? '';
  const entityCategory = selectedEntity?.entityCategory ?? 'source';

  // AI description from report context
  const aiDescription = report?.detections_info?.description || '';

  const maxChars = 100;
  const isTruncated = aiDescription.length > maxChars;
  const moreLessText = isExpanded ? 'less' : 'more';
  const aiDescriptionDisplay = isExpanded
    ? aiDescription
    : aiDescription.slice(0, maxChars) + (isTruncated ? '...' : '');

  // Transform severity level counts to handle undefined -> null conversion
  const {
    low = null,
    medium = null,
    high = null,
  } = detectionSummary?.detection_counts_by_severity_level || {};
  const severityLevelCounts =
    detectionSummary?.detection_counts_by_severity_level
      ? { low, medium, high }
      : null;

  // Format detection cards from the comprehensive query
  const detectionCards = useMemo(() => {
    if (!detectionSummary) {
      return {
        mostSevere: null,
        mostRecent: null,
        first: null,
      };
    }

    const formatDetection = (
      detection: DetectionSummaryDetection,
      content_id: string, // Adding on content_id to use in alert catalog link
      timestamp?: number | null,
    ): { date: string; detection: Detection } | null => {
      if (!detection) return null;

      // Only format valid timestamps (greater than 0 and not undefined/null)
      const formattedDate =
        timestamp && timestamp > 0
          ? moment.unix(timestamp).format('MMMM D, YYYY')
          : '—';

      return {
        date: formattedDate,
        detection: {
          alert_info: {
            alert_name: detection.alert_info?.alert_name || '—',
            content_id,
          },
          rank: {
            severity: detection.rank.severity,
            is_custom_severity: detection.rank.is_custom_severity ?? false,
          },
        } as Detection,
      };
    };

    return {
      mostSevere: formatDetection(
        detectionSummary.most_severe_detection,
        (detectionSummary.most_severe_detection?.alert_info as AlertInfo)
          ?.content_id || '',
        detectionSummary.most_severe_detection?.latest_start_timestamp,
      ),
      mostRecent: formatDetection(
        detectionSummary.most_recent_detection,
        (detectionSummary.most_recent_detection?.alert_info as AlertInfo)
          ?.content_id || '',
        detectionSummary.most_recent_detection?.latest_start_timestamp,
      ),
      first: formatDetection(
        detectionSummary.first_detection,
        (detectionSummary.first_detection?.alert_info as AlertInfo)
          ?.content_id || '',
        detectionSummary.first_detection?.earliest_start_timestamp,
      ),
    };
  }, [detectionSummary]);

  // Check if text is actually truncated
  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsTextTruncated(element.scrollWidth > element.offsetWidth);
    }
  }, [detectionSummary?.most_severe_alert_categories]);

  // Don't render if there's no detection summary data or no actual detections
  if (
    !detectionSummary ||
    !detectionSummary.total_detections ||
    detectionSummary.total_detections === 0
  ) {
    return null;
  }

  return (
    <Panel w="100%">
      <PanelHeader pb={0}>
        <PanelHeading>
          <HStack spacing={2}>
            <WarningSharpIcon />
            <Text>{copy.detections}</Text>
            {dateRangeDisplay && (
              <Text fontWeight="normal" color="text.placeholder">
                {dateRangeDisplay}
              </Text>
            )}
          </HStack>
        </PanelHeading>
      </PanelHeader>

      {aiDescription && !reportError && (
        <Box pb={2}>
          <HStack gap={2} align={isExpanded ? 'flex-start' : 'center'}>
            <Box flexShrink={0}>
              <ChatGPTIcon boxSize={5} />
            </Box>
            <Box>
              <Text textStyle="body-md">
                {parseBackticks(aiDescriptionDisplay)}
                {isTruncated && (
                  <Link
                    color="blue.400"
                    ml={1}
                    textStyle="body-md"
                    onClick={() => setIsExpanded(!isExpanded)}
                    cursor="pointer"
                  >
                    {moreLessText}
                  </Link>
                )}
              </Text>
            </Box>
          </HStack>
        </Box>
      )}

      {/* Summary Stats Row */}
      <HStack
        py={4}
        px={0}
        spacing={6}
        align="flex-start"
        justifyContent="space-between"
      >
        {/* Total Detections Severity Breakdown */}
        <VStack align="stretch" spacing={2} width="200px">
          <SeverityBreakdownBarChart
            severityLevelCounts={severityLevelCounts}
            tenant_entity={tenantEntity}
            tenant={tenantId}
            containerWidthPx={200}
            titleFormat="label-first"
            selectedEntityStart={selectedEntityStart}
            selectedEntityEnd={selectedEntityEnd}
          />
        </VStack>

        {/* Highest Severity Category */}
        <VStack align="stretch" spacing={2} width="200px">
          <Text fontSize="md" color="secondary">
            {copy.highestSeverityCategory}
          </Text>
          <Tooltip
            label={detectionSummary?.most_severe_alert_categories?.[0]}
            placement="top"
            hasArrow
            isDisabled={
              !detectionSummary?.most_severe_alert_categories?.[0] ||
              !isTextTruncated
            }
          >
            <Text ref={textRef} fontSize="md" isTruncated cursor="pointer">
              {detectionSummary?.most_severe_alert_categories?.[0] || '—'}
            </Text>
          </Tooltip>
        </VStack>

        {/* MITRE Tactics */}
        <VStack align="start" spacing={2} width="200px">
          <Text fontSize="md" color="secondary">
            {copy.mitreTactics}
          </Text>
          <MitreTacticsPopover
            mitreTacticCounts={
              detectionSummary?.detection_counts_by_mitre_tactics?.filter(
                (tactic): tactic is DetectionCountsByMitreTactic =>
                  tactic !== null && tactic !== undefined,
              ) ?? []
            }
            entityName={entityName}
            tenantEntity={tenantEntity}
            tenant={tenantId}
            start={selectedEntityStart}
            end={selectedEntityEnd}
          />
        </VStack>

        {/* Triage Status */}
        <VStack align="stretch" spacing={2} width="150px">
          <Text fontSize="md" color="secondary">
            {copy.triageStatus}
          </Text>
          <TriageStatusPopover
            value={{
              closed: detectionSummary?.detection_counts_by_status?.closed || 0,
              open: detectionSummary?.detection_counts_by_status?.open || 0,
            }}
            entityName={entityName}
            tenantId={tenantId}
            tenantEntity={tenantEntity}
            entityCategory={entityCategory}
            start={selectedEntityStart}
            end={selectedEntityEnd}
          />
        </VStack>
      </HStack>

      <HStack
        align="stretch"
        flexWrap="wrap"
        justifyContent="space-between"
        gap={4}
        px={0}
      >
        {[
          { key: 'mostSevere', title: copy.mostSevereDetection },
          { key: 'mostRecent', title: copy.latestDetection },
          { key: 'first', title: copy.firstDetection },
        ].map(({ key, title }) => {
          const card = detectionCards[key as keyof typeof detectionCards];
          return (
            card && (
              <DetectionSummaryInformationCard
                key={key}
                title={title}
                date={card.date}
                detection={card.detection}
              />
            )
          );
        })}
      </HStack>
      <Flex mt={6} flexDirection="column" gap={1}>
        <DetectionsActivity />
      </Flex>
    </Panel>
  );
};
